import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ChangeType, DurationType, ExecutionType, ChangePriority } from './types';
import { mocService } from './mocService';

interface Props {
  organizationId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const MocCreateModal: React.FC<Props> = ({ organizationId, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    change_type: 'TECHNICAL' as ChangeType,
    duration_type: 'PERMANENT' as DurationType,
    execution_type: 'PLANNED' as ExecutionType,
    priority: 'MEDIUM' as ChangePriority,
    target_department: 'Flight Operations',
    target_fleet_type: '',
    planned_start_date: new Date().toISOString().split('T')[0],
    planned_end_date: new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0],
    initiator_name: 'Lead Specialist',
    change_owner_name: 'Operations Manager',
    is_training_required: false,
    is_authority_approval_required: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await mocService.createChange(
        {
          ...form,
          initiator_id: 'USR-INITIATOR-01',
          change_owner_id: 'USR-OWNER-01',
          status: 'DRAFT'
        },
        organizationId
      );
      onSuccess();
    } catch (err) {
      alert('Failed to initiate change request: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-2xl">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Initiate Management of Change (MOC)</h3>
            <p>Propose a planned or emergency operational, technical, or organizational change</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div className="form-field">
              <label>Change Title *</label>
              <input
                type="text"
                required
                placeholder="e.g. B737 Fleet Electronic Flight Bag (EFB) Paperless Transition"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label>Description & Scope *</label>
              <textarea
                required
                rows={3}
                placeholder="Provide justification, objective, scope, and operational boundaries of this change..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Change Type</label>
                <select
                  value={form.change_type}
                  onChange={(e) => setForm({ ...form, change_type: e.target.value as any })}
                >
                  <option value="TECHNICAL">Technical / Systems</option>
                  <option value="OPERATIONAL">Operational</option>
                  <option value="ORGANIZATIONAL">Organizational</option>
                  <option value="FLEET">Fleet / Aircraft</option>
                  <option value="FACILITY">Facility / Infrastructure</option>
                  <option value="REGULATORY">Regulatory Compliance</option>
                  <option value="PROCEDURAL">Standard Operating Procedures</option>
                </select>
              </div>
              <div className="form-field">
                <label>Target Department *</label>
                <input
                  type="text"
                  required
                  value={form.target_department}
                  onChange={(e) => setForm({ ...form, target_department: e.target.value })}
                />
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-field">
                <label>Duration Type</label>
                <select
                  value={form.duration_type}
                  onChange={(e) => setForm({ ...form, duration_type: e.target.value as any })}
                >
                  <option value="PERMANENT">Permanent</option>
                  <option value="TEMPORARY">Temporary</option>
                </select>
              </div>
              <div className="form-field">
                <label>Execution Mode</label>
                <select
                  value={form.execution_type}
                  onChange={(e) => setForm({ ...form, execution_type: e.target.value as any })}
                >
                  <option value="PLANNED">Planned</option>
                  <option value="EMERGENCY">Emergency</option>
                </select>
              </div>
              <div className="form-field">
                <label>Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value as any })}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Planned Start Date *</label>
                <input
                  type="date"
                  required
                  value={form.planned_start_date}
                  onChange={(e) => setForm({ ...form, planned_start_date: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label>Planned Completion Date *</label>
                <input
                  type="date"
                  required
                  value={form.planned_end_date}
                  onChange={(e) => setForm({ ...form, planned_end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Initiator Name *</label>
                <input
                  type="text"
                  required
                  value={form.initiator_name}
                  onChange={(e) => setForm({ ...form, initiator_name: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label>Change Owner Name *</label>
                <input
                  type="text"
                  required
                  value={form.change_owner_name}
                  onChange={(e) => setForm({ ...form, change_owner_name: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-6 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs">
              <label className="flex items-center gap-2 cursor-pointer text-gray-700 font-medium">
                <input
                  type="checkbox"
                  checked={form.is_training_required}
                  onChange={(e) => setForm({ ...form, is_training_required: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Mandatory Training Required
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-gray-700 font-medium">
                <input
                  type="checkbox"
                  checked={form.is_authority_approval_required}
                  onChange={(e) => setForm({ ...form, is_authority_approval_required: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Regulatory Authority Approval Required
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Submit Change Proposal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
